import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, History, Pencil, Plus, Receipt, Wrench, X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  IncidentStatusBadge,
  invalidateIncidents,
  InvoiceStatusBadge,
  LogRepairModal,
  photoProblem,
  preparePhoto,
  toLocalInput,
} from '../../components/device-history';
import { PersonPicker } from '../../components/pickers';
import { useToast } from '../../components/toast';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Select,
  Textarea,
} from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { formatDate, formatDateTime, formatMoney, formatUtcDate, personName } from '../../lib/format';
import {
  INCIDENT_STATUS_LABEL,
  INCIDENT_TYPE_LABEL,
  incidentPhotoUrl,
  REPAIR_KIND_LABEL,
  REPAIR_KIND_TONE,
  uploadIncidentPhoto,
  type DamageInvoice,
  type IncidentDetail,
  type IncidentPhoto,
  type IncidentType,
} from '../../lib/incidents';
import type { PersonSummary } from '../../lib/types';

type IncidentUpdate = Partial<{ type: IncidentType; status: string; occurredAt: string; description: string; responsiblePersonId: string | null }>;

/** One incident: what happened, its photos, the repairs and invoices for it, and its status. */
export function IncidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [logging, setLogging] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [removingPhoto, setRemovingPhoto] = useState<IncidentPhoto | null>(null);

  const query = useQuery({ queryKey: ['incident', id], queryFn: () => api.get<IncidentDetail>(`/incidents/${id}`) });
  const onError = (err: unknown) => toast.error(errorMessage(err));

  const update = useMutation({
    mutationFn: (body: IncidentUpdate) => api.patch<IncidentDetail>(`/incidents/${id}`, body),
    onSuccess: (incident) => {
      queryClient.setQueryData(['incident', id], incident);
      invalidateIncidents(queryClient);
      toast.success('Incident updated');
    },
    onError,
  });
  const removePhoto = useMutation({
    mutationFn: (photo: IncidentPhoto) => api.delete(`/incidents/${id}/photos/${photo.id}`),
    onSuccess: () => {
      setRemovingPhoto(null);
      invalidateIncidents(queryClient);
      toast.success('Photo removed');
    },
    onError,
  });
  const createInvoice = useMutation({
    mutationFn: () => api.post<DamageInvoice>(`/incidents/${id}/invoices`),
    onSuccess: (invoice) => {
      invalidateIncidents(queryClient);
      navigate(`/invoices/${invoice.id}`);
    },
    onError,
  });

  const addPhotos = async (files: File[]) => {
    setUploading((count) => count + files.length);
    for (const file of files) {
      try {
        const prepared = await preparePhoto(file);
        await uploadIncidentPhoto(prepared.blob, prepared.fileName, id);
      } catch (err) {
        toast.error(`${file.name}: ${photoProblem(err)}`);
      } finally {
        setUploading((count) => count - 1);
      }
    }
    invalidateIncidents(queryClient);
  };

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const incident = query.data;
  const { permissions } = incident;
  const final = incident.status === 'CLOSED' || incident.status === 'DISMISSED';
  const statusHint =
    permissions.statuses.length <= 1 && final
      ? `This incident is ${INCIDENT_STATUS_LABEL[incident.status].toLowerCase()}. Only an administrator can reopen or change it.`
      : !permissions.statuses.includes('CLOSED')
        ? 'Only administrators can close or dismiss an incident.'
        : `Changed ${formatDateTime(incident.statusChangedAt)}`;
  const model = [incident.asset.manufacturer, incident.asset.model].filter(Boolean).join(' ');

  return (
    <>
      <PageHeader
        back={{ to: '/incidents', label: 'Incidents & billing' }}
        title={`Incident #${incident.number}`}
        meta={
          <>
            <Badge tone="amber">{INCIDENT_TYPE_LABEL[incident.type]}</Badge>
            <IncidentStatusBadge status={incident.status} />
          </>
        }
        actions={
          permissions.update && (
            <Button icon={<Pencil />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="What happened" />
            <div className="space-y-5 p-5">
              <p className="text-sm whitespace-pre-line text-slate-800">{incident.description}</p>
              <DescriptionList
                items={[
                  { label: 'When', value: formatDateTime(incident.occurredAt) },
                  {
                    label: 'Who had the device',
                    value: incident.responsiblePerson ? (
                      <Link to={`/people/${incident.responsiblePerson.id}`} className="text-brand-700 hover:underline">
                        {personName(incident.responsiblePerson)}
                      </Link>
                    ) : (
                      'Not set'
                    ),
                  },
                  { label: 'Reported by', value: `${incident.createdByName} · ${formatDateTime(incident.createdAt)}` },
                  {
                    label: 'Work order',
                    value: incident.workOrder ? (
                      <Link to={`/work-orders/${incident.workOrder.id}`} className="text-brand-700 hover:underline">
                        #{incident.workOrder.number} {incident.workOrder.title}
                      </Link>
                    ) : null,
                  },
                ]}
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Photos"
              description="Evidence of the damage"
              actions={
                permissions.addPhotos && (
                  <label
                    className={cn(
                      'inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50',
                      uploading > 0 && 'pointer-events-none opacity-60',
                    )}
                  >
                    <Camera className="size-4" />
                    {uploading > 0 ? 'Uploading…' : 'Add photos'}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={(event) => {
                        const files = [...(event.target.files ?? [])];
                        event.target.value = '';
                        if (files.length > 0) void addPhotos(files);
                      }}
                    />
                  </label>
                )
              }
            />
            {incident.photos.length === 0 ? (
              <EmptyState icon={<Camera />} title="No photos" description={permissions.addPhotos ? 'Add photos of the damage as evidence.' : undefined} />
            ) : (
              <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-4">
                {incident.photos.map((photo) => (
                  <figure key={photo.id} className="group relative">
                    <a
                      href={incidentPhotoUrl(incident.id, photo.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="block aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
                    >
                      <img src={incidentPhotoUrl(incident.id, photo.id)} alt={photo.fileName} loading="lazy" className="size-full object-cover" />
                    </a>
                    <figcaption className="mt-1 truncate text-xs text-slate-500">
                      {photo.uploadedByName} · {formatDate(photo.createdAt)}
                    </figcaption>
                    {photo.canDelete && (
                      <button
                        type="button"
                        onClick={() => setRemovingPhoto(photo)}
                        className="absolute top-1.5 right-1.5 rounded-full bg-slate-900/70 p-1 text-white hover:bg-slate-900"
                        aria-label={`Remove ${photo.fileName}`}
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </figure>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Repairs"
              actions={
                <Button size="sm" icon={<Wrench />} onClick={() => setLogging(true)}>
                  Log repair
                </Button>
              }
            />
            {incident.repairs.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500">No repairs yet. Resolving a linked repair ticket adds one.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {incident.repairs.map((repair) => (
                  <li key={repair.id} className="space-y-1 px-5 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={REPAIR_KIND_TONE[repair.kind]}>{REPAIR_KIND_LABEL[repair.kind]}</Badge>
                      <span className="text-xs text-slate-500">{formatUtcDate(repair.performedOn)}</span>
                      {repair.cost !== null && <span className="ml-auto text-sm font-medium text-slate-900">{formatMoney(repair.cost)}</span>}
                    </div>
                    <p className="text-sm font-medium text-slate-900">{repair.summary}</p>
                    {repair.details && <p className="text-sm whitespace-pre-line text-slate-600">{repair.details}</p>}
                    <p className="text-xs text-slate-500">
                      {[repair.vendor, repair.createdByName].filter(Boolean).join(' · ')}
                      {repair.workOrder && (
                        <>
                          {' · '}
                          <Link to={`/work-orders/${repair.workOrder.id}`} className="text-brand-700 hover:underline">
                            Work order #{repair.workOrder.number}
                          </Link>
                        </>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Invoices"
              description="What the family is billed"
              actions={
                permissions.createInvoice && (
                  <Button size="sm" icon={<Plus />} loading={createInvoice.isPending} onClick={() => createInvoice.mutate()}>
                    Create invoice
                  </Button>
                )
              }
            />
            {incident.invoices.length === 0 ? (
              <p className="px-5 py-4 text-sm text-slate-500">
                {incident.status === 'DISMISSED'
                  ? 'Dismissed incidents aren’t billed.'
                  : incident.responsiblePerson
                    ? 'No invoices yet.'
                    : 'Set who had the device to bill their family.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {incident.invoices.map((invoice) => (
                  <li key={invoice.id}>
                    <Link to={`/invoices/${invoice.id}`} className="flex items-center gap-3 px-5 py-3 text-sm hover:bg-slate-50">
                      <Receipt className="size-4 text-slate-400" />
                      <span className="flex-1 font-medium text-slate-900">Invoice #{invoice.number}</span>
                      <span className="text-slate-700">{formatMoney(invoice.total)}</span>
                      <InvoiceStatusBadge status={invoice.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-2 p-5">
              <Select
                value={incident.status}
                disabled={permissions.statuses.length <= 1 || update.isPending}
                onChange={(event) => update.mutate({ status: event.target.value })}
                aria-label="Status"
              >
                {permissions.statuses.map((status) => (
                  <option key={status} value={status}>
                    {INCIDENT_STATUS_LABEL[status]}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-slate-500">{statusHint}</p>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Device"
              actions={
                <Link to={`/assets/${incident.asset.id}`} className="text-sm font-medium text-brand-700 hover:text-brand-800">
                  Open
                </Link>
              }
            />
            <div className="space-y-1 p-5 text-sm">
              <p className="font-mono font-semibold text-slate-900">{incident.asset.assetTag}</p>
              <p className="text-slate-700">{incident.asset.name}</p>
              {model && model !== incident.asset.name && <p className="text-xs text-slate-500">{model}</p>}
              {incident.asset.serialNumber && <p className="text-xs text-slate-500">Serial {incident.asset.serialNumber}</p>}
              {incident.asset.purchasePrice !== null && <p className="text-xs text-slate-500">Bought for {formatMoney(incident.asset.purchasePrice)}</p>}
            </div>
          </Card>

          <Card>
            <CardHeader title="Activity" />
            {incident.history.length === 0 ? (
              <EmptyState icon={<History />} title="No activity recorded" />
            ) : (
              <ol className="space-y-4 p-5">
                {incident.history.map((entry) => (
                  <li key={entry.id} className="relative pl-5 text-sm">
                    <span className="absolute top-1.5 left-0 size-2 rounded-full bg-brand-400" />
                    <p className="text-slate-800">{entry.summary}</p>
                    <p className="text-xs text-slate-500">
                      {entry.actorName ?? 'System'} · {formatDateTime(entry.createdAt)}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>

      <EditIncidentModal
        incident={incident}
        open={editing}
        onClose={() => setEditing(false)}
        saving={update.isPending}
        onSave={(body) => update.mutate(body, { onSuccess: () => setEditing(false) })}
      />
      <LogRepairModal open={logging} onClose={() => setLogging(false)} assetId={incident.asset.id} incidentId={incident.id} />
      <ConfirmDialog
        open={!!removingPhoto}
        onClose={() => setRemovingPhoto(null)}
        onConfirm={() => removingPhoto && removePhoto.mutate(removingPhoto)}
        title="Remove this photo?"
        description="It comes out of the incident’s evidence."
        confirmLabel="Remove"
        loading={removePhoto.isPending}
      />
    </>
  );
}

function EditIncidentModal({
  incident,
  open,
  onClose,
  onSave,
  saving,
}: {
  incident: IncidentDetail;
  open: boolean;
  onClose(): void;
  onSave(body: IncidentUpdate): void;
  saving: boolean;
}) {
  const [type, setType] = useState<IncidentType>(incident.type);
  const [occurredAt, setOccurredAt] = useState(toLocalInput(incident.occurredAt));
  const [description, setDescription] = useState(incident.description);
  const [person, setPerson] = useState<PersonSummary | null>(incident.responsiblePerson);

  useEffect(() => {
    if (!open) return;
    setType(incident.type);
    setOccurredAt(toLocalInput(incident.occurredAt));
    setDescription(incident.description);
    setPerson(incident.responsiblePerson);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (Number.isNaN(Date.parse(occurredAt))) return;
    onSave({ type, occurredAt: new Date(occurredAt).toISOString(), description, responsiblePersonId: person?.id ?? null });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit incident #${incident.number}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form="edit-incident" variant="primary" loading={saving}>
            Save
          </Button>
        </>
      }
    >
      <form id="edit-incident" className="space-y-4" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="What happened" required>
            <Select value={type} onChange={(event) => setType(event.target.value as IncidentType)}>
              {(Object.keys(INCIDENT_TYPE_LABEL) as IncidentType[]).map((value) => (
                <option key={value} value={value}>
                  {INCIDENT_TYPE_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="When" required>
            <Input type="datetime-local" value={occurredAt} max={toLocalInput(new Date())} onChange={(event) => setOccurredAt(event.target.value)} required />
          </Field>
        </div>
        <Field as="div" label="Who had the device" hint="A damage invoice bills this person’s family">
          <PersonPicker value={person} onChange={setPerson} placeholder="Search for the student or staff member" />
        </Field>
        <Field label="Description" required>
          <Textarea rows={5} value={description} onChange={(event) => setDescription(event.target.value)} minLength={3} maxLength={5000} required />
        </Field>
      </form>
    </Modal>
  );
}
